import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppSettings } from './core/models/app.settings';
import { OverlayContainer } from '@angular/cdk/overlay';
import { CustomOverlayContainer } from './theme/utils/custom-overlay-container';
import { PerfectScrollbarConfigInterface } from 'ngx-perfect-scrollbar';
import { PERFECT_SCROLLBAR_CONFIG } from 'ngx-perfect-scrollbar';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PerfectScrollbarModule } from 'ngx-perfect-scrollbar';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { SharedModule } from './shared/shared.module';
import { routing } from './app-routing.module';
import { PagesComponent } from './pages/pages.component';
import { NotFoundComponent } from './pages/errors/not-found/not-found.component';
import { ErrorComponent } from './pages/errors/error/error.component';
import { VerticalMenuComponent } from './core/components/menu/vertical-menu/vertical-menu.component';
import { SidenavComponent } from './core/components/sidenav/sidenav.component';
import { FlagsMenuComponent } from './core/components/flags-menu/flags-menu.component';
import { UserMenuComponent } from './core/components/user-menu/user-menu.component';
import { CoreModule } from '@angular/flex-layout';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CustomHttpInterceptor, ErrorHttpInterceptor } from './core/interceptors';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { CustomMatPaginatorInt } from './core/angular-matrials/custom-mat-paginator-int ';
import { JwtModule } from '@auth0/angular-jwt';
import { NotAuthorizedComponent } from './pages/errors/not-authorized/not-authorized.component';
import { AngularLineawesomeModule, LaIconLibrary } from 'angular-line-awesome';
import {
  lasShower,
  lasKey,
  lasToilet,
  lasWater,
  lasFirstAid,
  lasWineBottle,
  lasDoorClosed,
  lasFutbol,
  lasRestroom,
} from 'angular-line-awesome/icons';

import {
  NgxMatDatetimePickerModule,
  NGX_MAT_DATE_FORMATS,
  NgxMatTimepickerModule,
  NgxMatNativeDateModule,
  NgxMatDateFormats,
} from '@angular-material-components/datetime-picker';
import { NgxMatMomentModule } from '@angular-material-components/moment-adapter';

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  wheelPropagation: true,
  suppressScrollX: true,
};

export const MY_FORMAT = 'YYYY-MM-DD hh:mm:ss A';
// If using Moment
const CUSTOM_MOMENT_FORMATS: NgxMatDateFormats = {
  parse: {
    dateInput: MY_FORMAT,
  },
  display: {
    dateInput: MY_FORMAT,
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

// AoT requires an exported function for factories
export const createTranslateLoader = (http: HttpClient) => {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
};

export function tokenGetter() {
  return localStorage.getItem('access_token');
}

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    PerfectScrollbarModule,
    CalendarModule.forRoot({
      provide: DateAdapter,
      useFactory: adapterFactory,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    JwtModule.forRoot({
      config: {
        tokenGetter,
      },
    }),
    SharedModule,
    CoreModule,
    AngularLineawesomeModule,
    routing,
  ],
  declarations: [
    AppComponent,
    PagesComponent,
    NotFoundComponent,
    ErrorComponent,
    SidenavComponent,
    VerticalMenuComponent,
    FlagsMenuComponent,
    UserMenuComponent,
    NotAuthorizedComponent,
  ],
  providers: [
    AppSettings,
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    { provide: OverlayContainer, useClass: CustomOverlayContainer },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomHttpInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorHttpInterceptor, multi: true },
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorInt },
    { provide: MY_FORMAT, useValue: CUSTOM_MOMENT_FORMATS },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(library: LaIconLibrary) {
    // Add an icon to the library for convenient access in other components
    library.addIcons([
      lasShower,
      lasKey,
      lasToilet,
      lasWater,
      lasFutbol,
      lasFirstAid,
      lasWineBottle,
      lasDoorClosed,
      lasRestroom,
    ]);
  }
}
